import type {
  GenerateAltTextRequest,
  GenerateAltTextResponse,
  MLAsset,
  SaveAltTextRequest
} from "@dealer-center/ddc-pages-alt-text-manager-interface";
import { useMutation, useQuery } from "@tanstack/react-query";

const queryKeyBase = "ddc-pages-alt-text-manager";

export function useGetImagesByAccountId(accountId: string | undefined) {
  return useQuery({
    queryKey: [queryKeyBase, "assets", accountId],
    queryFn: () => getAssetsByAccountId(accountId!),
    retry: 1,
    staleTime: 0,
    enabled: !!accountId
  });
}

export function useGenerateAltText() {
  return useMutation({
    mutationFn: ({
      signal,
      ...request
    }: GenerateAltTextRequest & { signal?: AbortSignal }) =>
      generateAltText(request, signal)
  });
}

export function useSaveAltText() {
  return useMutation({
    mutationFn: (request: SaveAltTextRequest) => saveAltText(request)
  });
}

const getAssetsByAccountId = async (accountId: string): Promise<MLAsset[]> => {
  const url = `/landing/nxlambda/altTextManager/assets/${accountId}`;
  const res = await fetch(url);
  if (res.ok) {
    const data = await res.json();
    return data;
  } else {
    console.error("Failed to fetch folders:", res.status, res.statusText);
    throw new Error(res.statusText);
  }
};

const generateAltText = async (
  request: GenerateAltTextRequest,
  signal?: AbortSignal
): Promise<GenerateAltTextResponse> => {
  const res = await fetch(
    "/landing/nxlambda/altTextManager/generate-alt-text",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(request),
      signal
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    const errorText = await res.json();
    throw new Error(errorText.error || res.statusText);
  }
};

const saveAltText = async (request: SaveAltTextRequest): Promise<string> => {
  const res = await fetch("/landing/nxlambda/altTextManager/save-alt-text", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(request)
  });
  if (res.ok) {
    return res.text();
  } else {
    throw new Error(res.statusText);
  }
};
